import { useParams, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useGameStore } from '../store/gameStore'
import Button from '../components/Button'
import Modal from '../components/Modal'
import FreeSeedClaim from '../components/garden/FreeSeedClaim'
import { ArrowLeft, Sprout } from 'lucide-react'
import * as Icons from 'lucide-react'
import { useI18n } from '../i18n/I18nProvider'

function Merchant() {
  const { t } = useI18n()
  const { merchantType } = useParams()
  const [items, setItems] = useState([])
  const [selectedItem, setSelectedItem] = useState(null)
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  
  const config = useGameStore((state) => state.config)
  const seedInventory = useGameStore((state) => state.seedInventory)
  const addToInventory = useGameStore((state) => state.addToInventory)
  const ownedBackgrounds = useGameStore((state) => state.ownedBackgrounds)
  const ownedTools = useGameStore((state) => state.ownedTools)
  const spendSeeds = useGameStore((state) => state.spendSeeds)
  const selectBackground = useGameStore((state) => state.selectBackground)
  const unlockTool = useGameStore((state) => state.unlockTool)
  const autoGardenerOwned = useGameStore((state) => state.autoGardenerOwned)
  const unlockAutoGardener = useGameStore((state) => state.unlockAutoGardener)
  const fertilizerActiveUntil = useGameStore((state) => state.fertilizerActiveUntil)
  
  // Monnaie de la boutique : uniquement les graines basiques ('default')
  const basicSeeds = seedInventory?.default || 0

  useEffect(() => {
    if (config && merchantType) {
      setItems(config.shop[merchantType] || [])
    }
  }, [config, merchantType])

  const handlePurchase = (item) => {
    setSelectedItem(item)
    setShowPurchaseModal(true)
  }

  const confirmPurchase = () => {
    if (!selectedItem) return
    
    const totalSeeds = seedInventory?.default || 0
    if (totalSeeds < selectedItem.price) {
      // Sécurité supplémentaire, même si le bouton est déjà désactivé
      return
    }

    const success = spendSeeds(selectedItem.price)
    if (!success) {
      return
    }

    // Appliquer l'achat
    if (selectedItem.type === 'background') {
      // Ajouter le background à la liste possédée et le sélectionner
      if (!ownedBackgrounds.includes(selectedItem.id)) {
        useGameStore.setState((state) => ({
          ownedBackgrounds: [...state.ownedBackgrounds, selectedItem.id],
        }))
      }
      selectBackground(selectedItem.id)
    } else if (selectedItem.type === 'gardener') {
      unlockAutoGardener(selectedItem.gardenerConfig || {})
    } else if (selectedItem.type === 'tool') {
      unlockTool(selectedItem.toolId, selectedItem.toolConfig || {})
    } else {
      // Pour les graines / objets, passer par addToInventory
      addToInventory(selectedItem)
    }

    setShowPurchaseModal(false)
  }

  const getMerchantTitle = () => {
    const titles = {
      tools: t('merchant.toolsMerchant'),
      seeds: t('merchant.seedsMerchant'),
      cosmetics: t('merchant.cosmeticsMerchant'),
    }
    return titles[merchantType] || t('merchant.defaultMerchant')
  }

  const getItemTranslation = (item) => {
    if (!item) return { name: '?', description: '' }

    // Déterminer le type d'élément et l'ID à utiliser pour la traduction
    let type = ''
    let itemId = item.id
    
    if (item.plantType) {
      // Pour les graines, on utilise le type de plante pour la traduction
      type = 'plants'
      itemId = item.plantType
    } else if (item.type === 'background') {
      type = 'backgrounds'
      // Pour les arrière-plans, on enlève le préfixe 'background_'
      itemId = itemId.replace('background_', '')
    } else if (item.type === 'pot') {
      type = 'pots'
    } else if (item.type === 'tool' || item.type === 'gardener') {
      type = 'tools'
    }

    // Essayer d'abord avec la traduction spécifique au type (plants, tools, etc.)
    const typeNameKey = `${type}.${itemId}.name`
    const typeDescKey = `${type}.${itemId}.description`
    
    // Puis essayer avec la traduction spécifique au magasin
    const shopNameKey = `shop.items.${type}.${itemId}.name`
    const shopDescKey = `shop.items.${type}.${itemId}.description`
    
    // Récupérer les traductions avec des valeurs par défaut
    const name = t(shopNameKey, { 
      defaultValue: t(typeNameKey, { 
        defaultValue: item.name || itemId.replace(/_/g, ' ')
      })
    })
    
    const description = t(shopDescKey, { 
      defaultValue: t(typeDescKey, { 
        defaultValue: item.description || ''
      })
    })
    
    return { 
      name: name.charAt(0).toUpperCase() + name.slice(1), // Capitalize first letter
      description: description
    }
  }

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-green-600"></div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/shop">
            <Button variant="outline" className="flex items-center gap-2">
              <ArrowLeft size={20} />
              {t('merchant.backButton')}
            </Button>
          </Link>
          <h1 className="text-4xl font-bold text-green-800">
            {getMerchantTitle()}
          </h1>
        </div>

        <div className="bg-green-100 rounded-lg p-4 mb-8 flex items-center justify-between">
          <span className="font-bold text-gray-700">{t('merchant.basicSeedsLabel')}</span>
          <div className="flex items-center gap-2">
            <Sprout size={24} className="text-green-600" />
            <span className="text-2xl font-bold text-green-600">{basicSeeds}</span>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">{t('merchant.noItemsAvailable')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Free Seed Claim Card - Only in seeds merchant */}
            {merchantType === 'seeds' && <FreeSeedClaim />}
            
            {items.map((item) => {
              const IconComponent = Icons[item.icon] || Sprout
              const canAfford = basicSeeds >= item.price
              const isBackground = item.type === 'background'
              const isGardener = item.type === 'gardener'
              const isTool = item.type === 'tool'
              const fertilizerActive = fertilizerActiveUntil && fertilizerActiveUntil > Date.now()
              const alreadyOwned = isBackground
                ? ownedBackgrounds.includes(item.id)
                : isGardener
                  ? autoGardenerOwned
                  : isTool
                    ? item.toolId === 'fertilizer'
                      ? !!fertilizerActive
                      : ownedTools.includes(item.toolId)
                    : false
              
              const translatedItem = getItemTranslation(item)
              
              return (
                <div
                  key={item.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden"
                >
                  {isBackground && item.gradient ? (
                    <div 
                      className="h-32 flex items-center justify-center"
                      style={{
                        background: `linear-gradient(to bottom, ${item.gradient[0]}, ${item.gradient[1]})`
                      }}
                    >
                      {alreadyOwned && (
                        <div className="bg-white/90 px-4 py-2 rounded-lg font-bold text-green-600">
                          ✓ {t('merchant.owned')}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gradient-to-r from-green-400 to-green-500 p-8 flex items-center justify-center">
                      <IconComponent size={64} className="text-white" />
                    </div>
                  )}
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {translatedItem.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {translatedItem.description}
                    </p>

                    {isGardener && item.gardenerConfig && (
                      <div className="text-xs text-green-700 font-medium mb-3">
                        {item.gardenerConfig.clicksPerTick || 1} {t('merchant.clickInfo')} {((item.gardenerConfig.intervalMs || 1000) / 1000).toFixed(1)}s
                      </div>
                    )}
                    {isTool && item.toolId === 'fertilizer' && fertilizerActive && (
                      <div className="text-xs text-amber-600 font-medium mb-3">
                        {t('merchant.activeEffect')}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Sprout size={20} className="text-green-600" />
                        <span className="text-xl font-bold text-green-600">
                          {item.price}
                        </span>
                      </div>
                      {!canAfford && !alreadyOwned && (
                        <span className="text-xs text-red-600 font-medium">
                          {t('merchant.notEnoughSeeds')}
                        </span>
                      )}
                      {alreadyOwned && (
                        <span className="text-xs text-green-600 font-medium">
                          {isGardener || isTool ? t('merchant.active') : t('merchant.alreadyOwned')}
                        </span>
                      )}
                    </div>
                    
                    <Button
                      onClick={() => handlePurchase(item)}
                      disabled={!canAfford || alreadyOwned}
                      className="w-full"
                    >
                      {alreadyOwned ? (isGardener ? t('merchant.active') : t('merchant.owned')) : t('merchant.buyButton')}
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Purchase Confirmation Modal */}
      <Modal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        title={t('merchant.confirmPurchaseTitle')}
        size="sm"
      >
        {selectedItem && (
          <>
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold mb-2">{getItemTranslation(selectedItem).name}</h3>
              <p className="text-gray-600 mb-4">{getItemTranslation(selectedItem).description}</p>
              <div className="flex items-center justify-center gap-2 text-2xl font-bold text-green-600">
                <Sprout size={24} />
                {selectedItem.price}
              </div>
            </div>
            
            <div className="flex gap-4">
              <Button
                onClick={confirmPurchase}
                className="flex-1"
              >
                {t('merchant.confirmButton')}
              </Button>
              <Button
                onClick={() => setShowPurchaseModal(false)}
                variant="secondary"
                className="flex-1"
              >
                {t('multiGarden.cancelButton')}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}

export default Merchant
